"use client";

import {
  AnimatePresence,
  LayoutGroup,
  motion,
  useReducedMotion,
} from "framer-motion";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { TaskKanbanCard } from "@/components/agents/tasks/task-kanban-card";
import { TaskKanbanColumnHeader } from "@/components/agents/tasks/task-kanban-column-header";
import { TaskKanbanColumnScrollFade } from "@/components/agents/tasks/task-kanban-column-scroll-fade";
import {
  TASK_KANBAN_COLUMN_TOP_RADIUS_CLASS,
  TASK_KANBAN_COLUMN_WIDTH_CLASS,
  taskKanbanColumnBottomFadeMaskStyle,
} from "@/components/agents/tasks/task-kanban-styles";
import {
  DEMO_KANBAN_ACCOUNTS,
  demoKanbanAccount,
} from "@/lib/onboarding/demo-kanban-accounts";
import type { AgentSummary } from "@/lib/agents/types";
import type { OrgMember } from "@/lib/org/queries";
import type { TaskListItem, TaskStatus } from "@/lib/tasks/queries";
import { cn } from "@/lib/utils";

const DEMO_MEMBER: OrgMember = {
  id: "demo-assignee",
  name: "Drew",
  email: "drew@kya.ai",
  avatarUrl: "/onboarding/avatar.jpeg",
  role: "member",
};

const DEMO_AGENTS: AgentSummary[] = [
  buildAgent("request-agent", "Request Agent"),
  buildAgent("meeting-follow-up", "Meeting Follow-up"),
  buildAgent("renewal-agent", "Renewal Agent"),
];

const COLUMNS: Array<{
  status: TaskStatus;
  title: string;
}> = [
  {
    status: "todo",
    title: "Backlog",
  },
  {
    status: "in_progress",
    title: "In Progress",
  },
  {
    status: "needs_review",
    title: "Review",
  },
];

const TASK_TEMPLATES: TaskTemplate[] = [
  {
    id: "login-board-request-summary",
    title: "Summarize any requests this customer made over the past month",
    accountId: "skims",
    agentId: "request-agent",
  },
  {
    id: "login-board-usage-summary",
    title: "Draft monthly usage summary for leadership",
    accountId: "sony",
    agentId: "meeting-follow-up",
  },
  {
    id: "login-board-support-follow-up",
    title: "Follow up on open support tickets from last week",
    accountId: "ikea",
    agentId: "request-agent",
  },
  {
    id: "login-board-invoices",
    title: "Reconcile outstanding invoices for Q1",
    accountId: "clay",
    agentId: "renewal-agent",
  },
  {
    id: "login-board-crm-notes",
    title: "Update CRM notes after discovery calls",
    accountId: "glossier",
    agentId: "meeting-follow-up",
  },
  {
    id: "login-board-renewal-proposal",
    title: "Send renewal proposal to Kalshi",
    accountId: "kalshi",
    agentId: "renewal-agent",
  },
  {
    id: "login-board-next-step-agenda",
    title: "Create next-step agenda from the latest stakeholder thread",
    accountId: "superpower",
    agentId: "request-agent",
  },
  {
    id: "login-board-expansion-risks",
    title: "Pull expansion risks from the latest customer emails",
    accountId: "skims",
    agentId: "request-agent",
  },
  {
    id: "login-board-meeting-summary",
    title: "Create a meeting summary with owners and deadlines",
    accountId: "sony",
    agentId: "meeting-follow-up",
  },
  {
    id: "login-board-renewal-readiness",
    title: "Check renewal readiness for accounts closing this month",
    accountId: "kalshi",
    agentId: "renewal-agent",
  },
  {
    id: "login-board-stakeholder-map",
    title: "Update the stakeholder map from recent conversations",
    accountId: "glossier",
    agentId: "request-agent",
  },
];

const BOARD_WIDTH_PX = 1052;
const BOARD_HEIGHT_PX = 600;
const BOARD_INSET_PX = 16;
const BOARD_CLIP_ALLOWANCE_PX = 150;
const BOARD_LOGIN_HEADING_OFFSET_PX = 160;
const MIN_BOARD_SCALE = 0.44;
const BOARD_TICK_MS = 1700;
const BOARD_ACTIONS = [
  "review_done",
  "progress_to_review",
  "todo_to_progress",
  "new_todo",
] as const;

type TaskTemplate = {
  id: string;
  title: string;
  accountId: string;
  agentId: string;
};

type BoardTask = TaskTemplate & {
  instanceId: string;
  status: TaskStatus;
};

type BoardState = {
  tasks: BoardTask[];
  nextTemplateIndex: number;
  nextInstanceSerial: number;
  step: number;
};

export function TaskBoardProductPreview() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.62);
  const [boardTop, setBoardTop] = useState<string | number>(
    `max(${BOARD_INSET_PX}px, calc(50% - ${BOARD_LOGIN_HEADING_OFFSET_PX}px))`,
  );
  const [hasMeasuredBoard, setHasMeasuredBoard] = useState(false);
  const [boardState, setBoardState] = useState(initialBoardState);
  const reduceMotion = useReducedMotion();

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateScale = () => {
      const availableWidth = container.clientWidth - BOARD_INSET_PX * 2;
      const nextScale = Math.max(
        MIN_BOARD_SCALE,
        (availableWidth + BOARD_CLIP_ALLOWANCE_PX) / BOARD_WIDTH_PX,
      );
      const headingAlignedTop =
        container.clientHeight / 2 - BOARD_LOGIN_HEADING_OFFSET_PX;
      const maxVisibleTop =
        container.clientHeight - BOARD_HEIGHT_PX * nextScale - BOARD_INSET_PX;

      setScale(nextScale);
      setBoardTop(
        Math.max(
          BOARD_INSET_PX,
          Math.min(headingAlignedTop, Math.max(BOARD_INSET_PX, maxVisibleTop)),
        ),
      );
      setHasMeasuredBoard(true);
    };

    updateScale();

    const observer = new ResizeObserver(updateScale);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (reduceMotion) return;

    const interval = window.setInterval(() => {
      setBoardState(advanceBoard);
    }, BOARD_TICK_MS);

    return () => window.clearInterval(interval);
  }, [reduceMotion]);

  const visibleTasks = reduceMotion
    ? initialBoardState().tasks
    : boardState.tasks;
  const tasksByStatus = COLUMNS.map((column) => ({
    ...column,
    tasks: visibleTasks.filter((task) => task.status === column.status),
  }));

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden bg-[#fcfcfd]"
    >
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(252,252,253,0.92)_0%,rgba(252,252,253,0.72)_44%,rgba(252,252,253,1)_100%)]" />
      <div
        className="absolute left-4 origin-top-left"
        style={{
          top: boardTop,
          width: BOARD_WIDTH_PX,
          height: BOARD_HEIGHT_PX,
          transform: `scale(${scale})`,
          opacity: hasMeasuredBoard ? 1 : 0,
          transition: "opacity 220ms ease-out",
        }}
      >
        <LayoutGroup id="login-task-board-preview">
          <div className="flex h-[600px] gap-4">
            {tasksByStatus.map((column) => (
              <PreviewColumn
                key={column.status}
                status={column.status}
                title={column.title}
                tasks={column.tasks}
                reduceMotion={!!reduceMotion}
              />
            ))}
          </div>
        </LayoutGroup>
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-44 bg-[linear-gradient(180deg,rgba(252,252,253,0),#fcfcfd_78%)]" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-36 bg-[linear-gradient(90deg,rgba(252,252,253,0),#fcfcfd_76%)]" />
    </div>
  );
}

function PreviewColumn({
  status,
  title,
  tasks,
  reduceMotion,
}: {
  status: TaskStatus;
  title: string;
  tasks: BoardTask[];
  reduceMotion: boolean;
}) {
  return (
    <div
      className={cn(
        "relative flex h-full flex-col gap-2 overflow-hidden",
        TASK_KANBAN_COLUMN_WIDTH_CLASS,
        TASK_KANBAN_COLUMN_TOP_RADIUS_CLASS,
      )}
    >
      <TaskKanbanColumnScrollFade status={status} fadeToColor="#fcfcfd" />

      <div className="relative z-10 shrink-0">
        <TaskKanbanColumnHeader
          status={status}
          title={title}
          count={tasks.length}
          centerTitle
        />
      </div>

      <div
        className="relative z-10 flex min-h-0 flex-1 flex-col gap-1 overflow-hidden px-1 pb-2"
        style={taskKanbanColumnBottomFadeMaskStyle}
      >
        <AnimatePresence initial={false}>
          {tasks.map((task) => (
            <motion.div
              key={task.instanceId}
              layout={reduceMotion ? false : "position"}
              layoutId={task.instanceId}
              initial={
                reduceMotion ? false : { opacity: 0, y: 10, scale: 0.985 }
              }
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.985 }}
              transition={{
                type: "spring",
                duration: 0.58,
                bounce: 0.08,
              }}
              className="shrink-0"
            >
              <TaskKanbanCard
                task={buildTask(task, status)}
                orgMembers={[DEMO_MEMBER]}
                accounts={DEMO_KANBAN_ACCOUNTS}
                agents={DEMO_AGENTS}
                onChanged={() => {}}
                onOpenDetail={() => {}}
                interactive={false}
                revealAgentWorking
                animateAgentWorkingGrow
                repeatAgentWorkingWave
                revealReadyForReview
                animateReadyForReviewGrow
                key={`${task.instanceId}-${status}`}
                hideAccountPillShadow
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function initialBoardState(): BoardState {
  return {
    tasks: [
      buildBoardTask(0, 0, "todo"),
      buildBoardTask(1, 1, "todo"),
      buildBoardTask(2, 2, "todo"),
      buildBoardTask(3, 3, "in_progress"),
      buildBoardTask(4, 4, "in_progress"),
      buildBoardTask(5, 5, "needs_review"),
      buildBoardTask(6, 6, "needs_review"),
    ],
    nextTemplateIndex: 7,
    nextInstanceSerial: 7,
    step: 0,
  };
}

function advanceBoard(state: BoardState): BoardState {
  for (let offset = 0; offset < BOARD_ACTIONS.length; offset += 1) {
    const action = BOARD_ACTIONS[(state.step + offset) % BOARD_ACTIONS.length]!;
    const next = applyBoardAction(state, action);
    if (next) {
      return { ...next, step: state.step + 1 };
    }
  }

  return { ...state, step: state.step + 1 };
}

function applyBoardAction(
  state: BoardState,
  action: (typeof BOARD_ACTIONS)[number],
): Omit<BoardState, "step"> | null {
  if (action === "review_done") {
    const reviewTask = state.tasks.find(
      (task) => task.status === "needs_review",
    );
    if (!reviewTask) return null;

    return {
      ...state,
      tasks: state.tasks.filter(
        (task) => task.instanceId !== reviewTask.instanceId,
      ),
    };
  }

  if (action === "progress_to_review") {
    const progressTask = state.tasks.find(
      (task) => task.status === "in_progress",
    );
    if (!progressTask) return null;

    return {
      ...state,
      tasks: state.tasks.map((task) =>
        task.instanceId === progressTask.instanceId
          ? { ...task, status: "needs_review" }
          : task,
      ),
    };
  }

  if (action === "todo_to_progress") {
    const todoTask = state.tasks.find((task) => task.status === "todo");
    if (!todoTask) return null;

    return {
      ...state,
      tasks: state.tasks.map((task) =>
        task.instanceId === todoTask.instanceId
          ? { ...task, status: "in_progress" }
          : task,
      ),
    };
  }

  const nextTask = buildBoardTask(
    state.nextTemplateIndex,
    state.nextInstanceSerial,
    "todo",
  );

  return {
    ...state,
    tasks: [...state.tasks, nextTask],
    nextTemplateIndex: state.nextTemplateIndex + 1,
    nextInstanceSerial: state.nextInstanceSerial + 1,
  };
}

function buildBoardTask(
  templateIndex: number,
  instanceSerial: number,
  status: TaskStatus,
): BoardTask {
  const template = TASK_TEMPLATES[templateIndex % TASK_TEMPLATES.length]!;
  return {
    ...template,
    instanceId: `${template.id}-${instanceSerial}`,
    status,
  };
}

function buildAgent(id: string, name: string): AgentSummary {
  return {
    id,
    orgId: "demo",
    createdByUserId: null,
    name,
    description: null,
    enabled: true,
    status: "active",
    hasScorecard: false,
    documentBody: null,
    inputContract: null,
    templateSlug: null,
    createdAt: "",
    updatedAt: "",
  };
}

function buildTask(def: BoardTask, status: TaskStatus): TaskListItem {
  const account = demoKanbanAccount(def.accountId);

  return {
    id: def.instanceId,
    title: def.title,
    description: null,
    status,
    type: "generic",
    origin: "agent",
    startAt: null,
    createdAt: "",
    updatedAt: "",
    account: account
      ? {
          id: account.id,
          name: account.name,
          domain: account.domain,
          logoUrl: account.logoUrl,
        }
      : null,
    assignee: {
      id: DEMO_MEMBER.id,
      name: DEMO_MEMBER.name,
      email: DEMO_MEMBER.email,
    },
    chatId: null,
    metadata: {},
    agentRun: null,
    lockedByAgent: status === "in_progress",
    agentId: def.agentId,
  };
}
